import React from 'react';
import { BarChart2, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  setErrors,
  errors,
  isCalculating,
  investmentStrategies
}) => {
  // Расчет текущих накоплений
  const currentSavings = parseFloat(formData.currentSavings) || 0;
  
  // Расчет текущего срока до пенсии в годах
  const yearsToRetirement = (parseFloat(formData.retirementAge) || 0) - (parseFloat(formData.currentAge) || 0);
  
  // Обработчик изменения текущих накоплений
  const handleCurrentSavingsChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      currentSavings: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.currentSavings) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.currentSavings;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения желаемого пассивного дохода
  const handleDesiredPassiveIncomeChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      desiredPassiveIncome: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.desiredPassiveIncome) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.desiredPassiveIncome;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения возраста выхода на пенсию
  const handleRetirementAgeChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      retirementAge: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.retirementAge) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.retirementAge;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения доходности инвестиций
  const handleInvestmentReturnChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      investmentReturn: newValue,
      investmentStrategy: 'custom' // При ручном изменении доходности выбираем "Собственную" стратегию
    }));
    
    // Очищаем ошибку если она есть
    if (errors.investmentReturn) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.investmentReturn;
        return newErrors;
      });
    }
  };
  
  // Предопределенные суммы накоплений для быстрого выбора
  const predefinedSavings = [
    { value: '0', label: '0 ₽' },
    { value: '500000', label: '500 тыс.' },
    { value: '1000000', label: '1 млн' },
    { value: '5000000', label: '5 млн' }
  ];
  
  // Предопределенные значения пассивного дохода для быстрого выбора
  const predefinedPassiveIncome = [
    { value: '50000', label: '50 тыс.' },
    { value: '100000', label: '100 тыс.' },
    { value: '150000', label: '150 тыс.' },
    { value: '200000', label: '200 тыс.' }
  ];
  
  // Предопределенные возрасты выхода на пенсию для быстрого выбора
  const predefinedRetirementAges = [
    { value: '45', label: '45 лет' },
    { value: '50', label: '50 лет' },
    { value: '55', label: '55 лет' },
    { value: '60', label: '60 лет' }
  ];
  
  // Функция расчета
  const calculateFinancialIndependence = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрая настройка параметров
      </h3>
      
      <div className="space-y-5">
        {/* Текущие накопления */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="currentSavingsSlider" className="text-gray-600 dark:text-gray-400">Текущие накопления</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(parseFloat(formData.desiredPassiveIncome) || 0)}/мес</span>
          </div>
          <input
            type="range"
            id="desiredPassiveIncomeSlider"
            min="30000"
            max="500000"
            step="5000"
            value={parseFloat(formData.desiredPassiveIncome) || 0}
            onChange={handleDesiredPassiveIncomeChange}
            aria-label="Желаемый пассивный доход"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными суммами пассивного дохода */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedPassiveIncome.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, desiredPassiveIncome: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.desiredPassiveIncome === amount.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Возраст выхода на пенсию */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="retirementAgeSlider" className="text-gray-600 dark:text-gray-400">Возраст выхода на пенсию</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.retirementAge} лет (через {yearsToRetirement} {pluralize(yearsToRetirement, 'год', 'года', 'лет')})
            </span>
          </div>
          <input
            type="range"
            id="retirementAgeSlider"
            min={Math.max(parseFloat(formData.currentAge) + 1 || 31, 30)}
            max="80"
            step="1"
            value={formData.retirementAge}
            onChange={handleRetirementAgeChange}
            aria-label="Возраст выхода на пенсию"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными возрастами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedRetirementAges.map(age => (
              <button
                key={age.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, retirementAge: age.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.retirementAge === age.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                disabled={parseFloat(age.value) <= parseFloat(formData.currentAge)}
              >
                {age.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Доходность инвестиций */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="investmentReturnSlider" className="text-gray-600 dark:text-gray-400">Доходность инвестиций</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.investmentReturn}%</span>
          </div>
          <input
            type="range"
            id="investmentReturnSlider"
            min="3"
            max="25"
            step="0.5"
            value={formData.investmentReturn}
            onChange={handleInvestmentReturnChange}
            aria-label="Доходность инвестиций (%)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Предопределенные стратегии инвестирования */}
          <div className="grid grid-cols-3 gap-1 mt-2">
            {investmentStrategies.slice(0, 3).map(strategy => (
              <button
                key={strategy.id}
                type="button"
                onClick={() => setFormData(prev => ({ 
                  ...prev, 
                  investmentStrategy: strategy.id,
                  investmentReturn: strategy.expectedReturn.toString()
                }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.investmentStrategy === strategy.id 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                <div className="font-medium">{strategy.name}</div>
                <div className="text-xs opacity-75">{strategy.expectedReturn}%</div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Инфляция */}
        <div className="flex flex-col">
          <label className="text-sm text-gray-600 dark:text-gray-400 mb-1">Учет инфляции</label>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.useInflationAdjusted}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                useInflationAdjusted: e.target.checked 
              }))}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учитывать инфляцию ({formData.expectedInflation}%)
            </span>
          </label>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateFinancialIndependence}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать
        </Button>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default QuickAdjustmentSidebar;